import { type ComponentProps, type FC } from "react";
import { PublishStatusLabel } from "@clientFrontend/features/publication/components/PublishStatusLabel";
import { ApprovalStatusLabel } from "@clientFrontend/features/publication/components/ApprovalStatusLabel";
import { Icon } from "@root/shared/components/Icon";
import { Link } from "@root/shared/components/Link";
import { EmploymentLabels } from "./uiParts/EmploymentLabels";
import { NewArrivalSortTicketLabel } from "./uiParts/NewArrivalSortTicketLabel";
import * as styles from "./styles.css";

type ItemProps = {
  publishStatus: ComponentProps<typeof PublishStatusLabel>["publishStatus"];
  enterableFlag: boolean;
  approvalStatus: ComponentProps<typeof ApprovalStatusLabel>["approvalStatus"];
  code: string;
  title: string | null;
  shopNames: string[];
  occupationName: string | null;
  businessType: string | null;
  startPublishingDate: string | null;
  belowMinWageFlag: boolean;
  newArrivalSortTicketStatus: "none" | "now" | "future";
  availableNewArrivalSortTicket: boolean;
  newArrivalSortTicketStartDate: string | null;
  draftCreatedAt?: string;
  urls: {
    showUrl: string;
    editStep1Url: string;
    editStep2Url: string;
    editShopConditionUrl: string;
    copyUrl: string;
    cancelSortTicketUrl: string;
    cancelPendingUrl: string;
    destroyDraftUrl: string;
  };
  canEdit: boolean;
  canCancelPending: boolean;
  canCopy: boolean;
  canDestroyDraft: boolean;
  employments:
    ("regularMember" | "other" | "contractEmployee" | "businessConsignment" | "arbeit")[] | [];
};

type Props = {
  item: ItemProps;
  canEditShopCondition: boolean;
  site: "biyo" | "kaigo";
};

export const WorkTableItem: FC<Props> = ({ item, canEditShopCondition, site }) => {
  const clientCopyConfirmText = () => {
    const busiOccu =
      item.businessType && item.occupationName
        ? `（${item.businessType}×${item.occupationName}）`
        : "";
    let approvalStatusText = "";
    if (item.approvalStatus === "none") {
      approvalStatusText = "(下書き)";
    } else if (item.approvalStatus === "pending") {
      approvalStatusText = "(申請中)";
    }

    return `求人番号【${item.code}】${busiOccu}${approvalStatusText}の内容を元に作成しますか？`;
  };

  return (
    <>
      <td className={styles.workStatus}>
        <PublishStatusLabel publishStatus={item.publishStatus} enterableFlag={item.enterableFlag} />
        <br />
        <ApprovalStatusLabel approvalStatus={item.approvalStatus} />
        <br />
        {item.belowMinWageFlag && <span className={styles.belowMinWageLabel}>最低賃金割れ</span>}
      </td>
      <td className={styles.description}>
        <span className={styles.code}>{item.code}</span>
        <p className={styles.title}>{item.title ? item.title : "設定なし"}</p>
      </td>
      <td className={styles.businessType}>
        {item.occupationName && item.businessType && (
          <span>
            {item.businessType}×{item.occupationName}
          </span>
        )}
        <EmploymentLabels employments={item.employments} site={site} />
        {item.newArrivalSortTicketStatus !== "none" && (
          <div>
            <NewArrivalSortTicketLabel withinUse={item.newArrivalSortTicketStatus === "now"} />
          </div>
        )}
        <div>
          {!canEditShopCondition && (
            <Link href={item.urls.editStep2Url} theme="tertiary" className={styles.step2Link}>
              <Icon name="edit" />
              <span className={styles.oldBtnText}>雇用形態を編集する</span>
            </Link>
          )}
        </div>
      </td>
      <td className={styles.dateInfo}>
        <div>{item.startPublishingDate ? item.startPublishingDate : "----/--/--"}</div>
        {item.draftCreatedAt && (
          <div className={styles.draftCreatedAtText}>({item.draftCreatedAt})</div>
        )}
      </td>
      <td className={styles.actions}>
        {item.canEdit && (
          <a className={styles.actionLink} href={item.urls.editStep1Url}>
            <Icon name="edit" />
            {canEditShopCondition && "基本情報を"}
            編集
          </a>
        )}
        {canEditShopCondition && (
          <a className={styles.actionLink} href={item.urls.editShopConditionUrl}>
            <Icon name="edit" />
            給与と勤務時間を編集
          </a>
        )}
        {item.canCancelPending && (
          <a
            className={styles.actionLink}
            href={item.urls.cancelPendingUrl}
            data-confirm="申請を取り消します。本当によろしいですか？"
            data-method="patch"
          >
            <Icon name="cross-bold" />
            申請取り消し
          </a>
        )}
        {item.canDestroyDraft && (
          <a
            className={styles.actionLink}
            href={item.urls.destroyDraftUrl}
            data-confirm="下書きを削除しますか？"
            data-method="delete"
          >
            <Icon name="deletion" />
            下書き削除
          </a>
        )}
        {item.canCopy && (
          <a
            className={styles.actionLink}
            href={item.urls.copyUrl}
            data-confirm={clientCopyConfirmText()}
          >
            <Icon name="copy" />
            コピーして作成
          </a>
        )}
        {item.newArrivalSortTicketStatus === "future" && (
          <a
            className={styles.actionLink}
            href={item.urls.cancelSortTicketUrl}
            data-confirm={`${item.newArrivalSortTicketStartDate}に使用予定の新着チケットを取り消します。本当によろしいですか？`}
            data-method="patch"
          >
            <Icon name="cross-bold" />
            新着チケット取り消し
          </a>
        )}
        {item.availableNewArrivalSortTicket && (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <a href="" className={`${styles.actionLink} sort-ticket-btn`} data-code={item.code}>
            <Icon name="mark-ticket-outline" />
            新着チケット使用
          </a>
        )}
      </td>
      <td className={styles.showDetail}>
        <Link href={item.urls.showUrl} theme="tertiary" className={styles.showDetailLink}>
          求人の詳細
        </Link>
      </td>
    </>
  );
};
